import type { Author } from '@bulknutrients/bulk-shared-lib/interfaces/Expert';
import type { MetaData } from '@bulknutrients/bulk-shared-lib/interfaces/MetaData';
import {
  getAllAuthorSlugs,
  getAuthorData,
  getAuthorMetaData,
  getPreviewAuthorData,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/authors';
import { getRedirect } from '@bulknutrients/bulk-shared-lib/lib/sanity/redirect';
import { AuthorSocialLinks } from '@components/Author/AuthorSocialLinks';
import { BlogCardList } from '@components/Blog/BlogCardList';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Container } from '@components/Container';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { StandardLayout } from '@components/Layouts';
import { CustomImage } from '@components/Media/Image';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useLocalStorage } from '@hooks/useLocalStorage';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { type ReactElement, useEffect } from 'react';

export default function AuthorPage({
  authorData,
  meta,
  isPreview,
}: {
  authorData: Author;
  meta: MetaData;
  isPreview: boolean;
}): ReactElement {
  const { setRecentlyViewedPage } = useLocalStorage();

  useEffect(() => {
    setRecentlyViewedPage({
      page: { slug: authorData.slug, title: authorData.name },
      viewedTime: Date.now(),
    });
  }, [authorData, setRecentlyViewedPage]);

  return (
    <>
      <Head>
        <title>{meta.metaTitle}</title>
        <meta name="title" content={meta.metaTitle} />
        <meta name="description" content={meta.metaDescription} />
        {/* <meta
          property="og:url"
          content={`https://www.bulknutrients.com.au${blogData.slug.current}`}
        />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={blogData.title} />
        {blogData.summary !== null && (
          <meta
            property="og:description"
            content={portableTextToPlainText(blogData.summary)}
          />
        )}
        <meta
          property="og:image"
          content={urlFor(blogData.featuredImage?.asset).url() as string}
        /> */}
        {meta.noIndex && <meta name="robots" content="noindex" />}
        <link
          rel="canonical"
          href={
            meta.canonical
              ? meta.canonical
              : `${process.env.NEXT_PUBLIC_SERVER_URL}${authorData.slug}`
          }
        />
      </Head>
      <Container>
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            {
              title: 'Science, Health And Nutrition Experts',
              path: '/experts',
            },
          ]}
        />
      </Container>
      <Container as="main" id="main-content" className="mb-12.5">
        <div className="my-0 max-w-narrow-page justify-self-center">
          <h1>{authorData.name}</h1>
          {authorData.image && (
            <CustomImage
              image={authorData.image}
              width={300}
              height={300}
              round
            />
          )}
          <AuthorSocialLinks author={authorData} />
          {authorData.description && (
            <PortableTextBlockRenderer content={authorData.description} />
          )}
          {authorData.ambassadorLink && (
            <TextLink href={authorData.ambassadorLink}>
              View more about {authorData.name}, including their recommended
              products
            </TextLink>
          )}
        </div>
        <HorizontalDivider />
        <h2 className="pb-4 italic">Bulk Blog Posts by {authorData.name}</h2>
        <BlogCardList author={authorData._id} />
      </Container>
    </>
  );
}

AuthorPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = await getAllAuthorSlugs();
  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({
  params,
  preview,
  previewData,
}) => {
  if (preview) {
    // This is preview mode
    const authorData = await getPreviewAuthorData(
      (previewData as { id: string }).id
    );

    const meta = await getAuthorMetaData(authorData._id);

    return {
      props: {
        authorData,
        meta,
        isPreview: true,
      },
    };
  }

  const authorData = await getAuthorData(params?.author?.toString() as string);

  if (!authorData) {
    // try redirect
    const redirect = await getRedirect(
      `/experts/${params?.author?.toString()}` as string
    );
    if (redirect) {
      return {
        redirect: {
          destination: redirect.newUrl,
          permanent: redirect.permanent,
        },
      };
    }

    // fallback failed
    return {
      notFound: true,
    };
  }

  const meta = await getAuthorMetaData(authorData._id);

  return {
    props: {
      authorData,
      meta,
      isPreview: false,
    },
  };
};
